import * as THREE from 'three'
import { CATEGORY_COLOR, SETTLEMENT_COLOR } from '../content/attractionMeta'
import type { Attraction, Settlement } from '../types'
import { latLonToWorld } from './geo'

export type PoiKind = 'attraction' | 'settlement'

export interface PoiEntry {
  id: string
  kind: PoiKind
  name: string
  tier: 'major' | 'minor'
  position: THREE.Vector3
  attraction?: Attraction
  settlement?: Settlement
  marker: THREE.Group
}

export interface PoiLayer {
  group: THREE.Group
  entries: PoiEntry[]
  /** Raycast targets, so a marker can also be tapped directly. */
  pickables: THREE.Object3D[]
  nearest: (focus: THREE.Vector3, cameraDistance: number) => PoiEntry | null
  setActive: (id: string | null) => void
  update: (elapsed: number, focus: THREE.Vector3, cameraDistance: number) => void
  entryById: (id: string) => PoiEntry | undefined
}

interface TierSpec {
  shaft: number
  radius: number
  ring: number
  glyph: number
  /** Distance from the look target at which the marker starts fading in. */
  reveal: number
  /** How close the look target must be to offer an interaction. */
  proximity: number
  baseOpacity: number
}

const TIERS: Record<'major' | 'minor' | 'settlement', TierSpec> = {
  major: { shaft: 4.4, radius: 0.17, ring: 1.15, glyph: 0.42, reveal: Infinity, proximity: 55, baseOpacity: 0.5 },
  minor: { shaft: 1.6, radius: 0.1, ring: 0.55, glyph: 0.24, reveal: 130, proximity: 20, baseOpacity: 0.42 },
  settlement: { shaft: 0.9, radius: 0.12, ring: 0.8, glyph: 0.3, reveal: 210, proximity: 45, baseOpacity: 0.44 },
}

function specFor(entry: PoiEntry): TierSpec {
  if (entry.kind === 'settlement') return TIERS.settlement
  return entry.tier === 'major' ? TIERS.major : TIERS.minor
}

function buildMarker(spec: TierSpec, color: number, settlement: boolean): THREE.Group {
  const group = new THREE.Group()
  const tint = new THREE.Color(color)

  const shaftMaterial = new THREE.MeshBasicMaterial({
    color: tint,
    transparent: true,
    opacity: spec.baseOpacity,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    side: THREE.DoubleSide,
  })
  const shaft = new THREE.Mesh(
    new THREE.CylinderGeometry(spec.radius * 0.35, spec.radius, spec.shaft, 8, 1, true),
    shaftMaterial,
  )
  shaft.position.y = spec.shaft / 2
  group.add(shaft)

  const ringMaterial = new THREE.MeshBasicMaterial({
    color: tint,
    transparent: true,
    opacity: spec.baseOpacity * 0.8,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    side: THREE.DoubleSide,
  })
  const ring = new THREE.Mesh(
    new THREE.RingGeometry(spec.ring * 0.62, spec.ring, settlement ? 4 : 24),
    ringMaterial,
  )
  ring.rotation.x = -Math.PI / 2
  ring.position.y = 0.05
  group.add(ring)

  const glyphMaterial = new THREE.MeshBasicMaterial({
    color: tint,
    transparent: true,
    opacity: Math.min(1, spec.baseOpacity + 0.3),
    depthWrite: false,
  })
  const glyph = new THREE.Mesh(
    settlement
      ? new THREE.BoxGeometry(spec.glyph * 1.5, spec.glyph, spec.glyph * 1.5)
      : new THREE.OctahedronGeometry(spec.glyph),
    glyphMaterial,
  )
  glyph.position.y = spec.shaft + spec.glyph * 1.4
  group.add(glyph)

  // The pick target is an invisible cylinder covering the whole marker, so
  // tapping anywhere on the beacon registers rather than only the thin glyph.
  const hit = new THREE.Mesh(
    new THREE.CylinderGeometry(Math.max(spec.ring, 0.9), Math.max(spec.ring, 0.9), spec.shaft + 1.2, 6),
    new THREE.MeshBasicMaterial({ visible: false }),
  )
  hit.position.y = (spec.shaft + 1.2) / 2
  group.add(hit)

  group.userData.shaft = shaft
  group.userData.ring = ring
  group.userData.glyph = glyph
  group.userData.hit = hit
  return group
}

export function createPointsOfInterest(input: {
  attractions: Attraction[]
  settlements: Settlement[]
  getHeight: (x: number, z: number) => number
  /** Attractions already represented by an authored hotspot pin. */
  excludeAttractionIds: Set<string>
}): PoiLayer {
  const group = new THREE.Group()
  group.name = 'points-of-interest'
  const entries: PoiEntry[] = []
  const pickables: THREE.Object3D[] = []

  const place = (
    id: string,
    kind: PoiKind,
    name: string,
    tier: 'major' | 'minor',
    location: { lat: number; lon: number },
    color: number,
    attraction?: Attraction,
    settlement?: Settlement,
  ) => {
    const { x, z } = latLonToWorld(location.lat, location.lon)
    const position = new THREE.Vector3(x, input.getHeight(x, z), z)
    const spec = kind === 'settlement' ? TIERS.settlement : tier === 'major' ? TIERS.major : TIERS.minor
    const marker = buildMarker(spec, color, kind === 'settlement')
    marker.position.copy(position)
    marker.userData.poiId = id
    const hit = marker.userData.hit as THREE.Mesh
    hit.userData.poiId = id
    group.add(marker)
    pickables.push(hit)
    entries.push({ id, kind, name, tier, position, attraction, settlement, marker })
  }

  for (const attraction of input.attractions) {
    if (input.excludeAttractionIds.has(attraction.id)) continue
    place(
      attraction.id,
      'attraction',
      attraction.name,
      attraction.tier,
      attraction.location,
      CATEGORY_COLOR[attraction.category] ?? 0xf0c27a,
      attraction,
    )
  }

  for (const settlement of input.settlements) {
    place(
      settlement.id,
      'settlement',
      settlement.name,
      'minor',
      settlement.location,
      SETTLEMENT_COLOR,
      undefined,
      settlement,
    )
  }

  let activeId: string | null = null
  const byId = new Map(entries.map((entry) => [entry.id, entry]))

  const nearest = (focus: THREE.Vector3, cameraDistance: number): PoiEntry | null => {
    // Far overview still allows majors when the look-target is on them; only
    // suppress minor clutter when the camera is high.
    let best: PoiEntry | null = null
    let bestDistance = Infinity
    for (const entry of entries) {
      const distance = entry.position.distanceTo(focus)
      const spec = specFor(entry)
      if (distance > spec.proximity) continue
      if (cameraDistance > 420 && entry.tier !== 'major' && entry.kind !== 'settlement') {
        continue
      }
      if (distance < bestDistance) {
        bestDistance = distance
        best = entry
      }
    }
    return best
  }

  const update = (elapsed: number, focus: THREE.Vector3, cameraDistance: number) => {
    for (const entry of entries) {
      const spec = specFor(entry)
      const distance = entry.position.distanceTo(focus)
      const active = entry.id === activeId

      let presence = 1
      if (spec.reveal !== Infinity) {
        presence = THREE.MathUtils.clamp(1 - (distance - spec.reveal * 0.6) / (spec.reveal * 0.4), 0, 1)
      }
      if (presence <= 0.001 && !active) {
        entry.marker.visible = false
        continue
      }
      entry.marker.visible = true

      const approach = THREE.MathUtils.clamp(1 - distance / (spec.proximity * 2.2), 0, 1)
      const pulse = active ? 0.75 + Math.sin(elapsed * 3.4) * 0.25 : 0
      const opacity = Math.min(
        1,
        spec.baseOpacity * presence + approach * 0.35 + pulse * 0.35,
      )

      // Beacons keep a readable screen size across a 44 km stage.
      const zoom = THREE.MathUtils.clamp(cameraDistance / 190, 0.62, 2.3)
      entry.marker.scale.setScalar(zoom * (active ? 1.16 : 1))

      const shaft = entry.marker.userData.shaft as THREE.Mesh<
        THREE.CylinderGeometry,
        THREE.MeshBasicMaterial
      >
      const ring = entry.marker.userData.ring as THREE.Mesh<
        THREE.RingGeometry,
        THREE.MeshBasicMaterial
      >
      const glyph = entry.marker.userData.glyph as THREE.Mesh<
        THREE.BufferGeometry,
        THREE.MeshBasicMaterial
      >
      shaft.material.opacity = opacity
      ring.material.opacity = opacity * 0.85
      glyph.material.opacity = Math.min(1, opacity + 0.25)
      ring.rotation.z = elapsed * (active ? 0.55 : 0.16)
      glyph.rotation.y = elapsed * (active ? 1.5 : 0.5)
      glyph.position.y =
        spec.shaft + spec.glyph * 1.4 + Math.sin(elapsed * 1.6 + entry.position.x) * 0.12
    }
  }

  return {
    group,
    entries,
    pickables,
    nearest,
    setActive: (id) => {
      activeId = id
    },
    update,
    entryById: (id) => byId.get(id),
  }
}
